import React, { useEffect, useRef, useState } from "react";
import hljs from "highlight.js";
import ReactQuill, { Quill } from "react-quill-new";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  createBlogApi,
  getAllBlogsApi,
  getBlogCategoriesApi,
  getBlogTagsApi,
  IMG_URL,
  updateBlogApi,
} from "../../helper/api_helper";
import { Icon } from "@iconify/react/dist/iconify.js";

import toast from "react-hot-toast";
const EditBlog = () => {
  const Link = Quill.import("formats/link");
  class CustomLink extends Link {
    static create(value) {
      const node = super.create(value);
      value = this.sanitize(value);
      node.setAttribute("href", value);
      if (value.startsWith("http://") || value.startsWith("https://")) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
      return node;
    }
  }
  Quill.register(CustomLink, true);

  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
    }));
    setImagePreview(null);

    const fileInput = document.getElementById("upload-file");
    if (fileInput) {
      fileInput.value = "";
    }
  };
  const quillRef = useRef(null);
  const [value, setValue] = useState(``);
  const [isHighlightReady, setIsHighlightReady] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category_id: "",
    blog_tag_ids: [],
    description: "",
    image: null,
    metaTitle: "",
    metaDescription: "",
    altTag: "",
    isScheduled: false,
    scheduledDate: "",
  });

  useEffect(() => {
    hljs?.configure({
      languages: [
        "javascript",
        "ruby",
        "python",
        "java",
        "csharp",
        "cpp",
        "go",
        "php",
        "swift",
      ],
    });
  }, []);

  const handleSave = () => {
    const editorContent = quillRef.current.getEditor().root.innerHTML;
  };

  const linkHandler = () => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();

    if (range) {
      if (range.length > 0) {
        const href = prompt(
          "Enter the link URL:\n(Examples: / or /services or https://example.com)",
          "/"
        );

        if (href) {
          quill.formatText(range.index, range.length, "link", href);
        }
      } else {

        const linkText = prompt("Enter the text to display:", "");

        if (linkText) {
          const href = prompt(
            "Enter the link URL:\n(Examples: / or /services or https://example.com)",
            "/"
          );

          if (href) {
            quill.insertText(range.index, linkText, "link", href);
            quill.setSelection(range.index + linkText.length);
          }
        }
      }
    }
  };

  const modules = isHighlightReady
    ? {
      syntax: {
        highlight: (text) => hljs?.highlightAuto(text).value,
      },
      clipboard: {
        matchVisual: false,
      },
      toolbar: {
        container: "#toolbar-container",
        handlers: {
          link: linkHandler,
        },
      },
    }
    : {
      clipboard: {
        matchVisual: false,
      },
      toolbar: {
        container: "#toolbar-container",
      },
    };

  const formats = [
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "header",
    "blockquote",
    "code-block",
    "list",
    "indent",
    "direction",
    "align",
    "link",
    "image",
    "video",
    "formula",
  ];

  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [blogTags, setBlogTags] = useState([]);
  const [creatingBlog, setCreatingBlog] = useState(false);

  const [isTagsLoading, setIsTagsLoading] = useState(false);

  const getAllBlogCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      const response = await getBlogCategoriesApi();

      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to fetch categories");
      setCategories([]);
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  useEffect(() => {
    getAllBlogCategories();
  }, []);

  const getAllBlogTags = async () => {
    try {
      setIsTagsLoading(true);
      const response = await getBlogTagsApi();

      setBlogTags(response.data);
    } catch (error) {
      toast.error("Failed to fetch tags");
      setBlogTags([]);
    } finally {
      setIsTagsLoading(false);
    }
  };

  useEffect(() => {
    getAllBlogCategories();
    getAllBlogTags();

    // Add clipboard matcher to strip font and size on paste
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        delta.ops.forEach((op) => {
          if (op.attributes) {
            delete op.attributes.font;
            delete op.attributes.size;
            delete op.attributes.header;
            delete op.attributes.color;
            delete op.attributes.background;
          }
        });
        return delta;
      });
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      description: content,
    }));
  };

  const createBlog = async () => {
    try {
      setCreatingBlog(true);

      const formDataToSend = new FormData();
      formDataToSend.append("id", blogId);
      let cleanSlug =
        formData.slug?.trim() || formData.blog_title?.trim() || "";

      cleanSlug = cleanSlug
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      formDataToSend.append("slug", cleanSlug);
      formDataToSend.append("title", formData.title);
      formDataToSend.append("blogCategoryId", formData.blog_category_id);
      formDataToSend.append("metaTitle", formData.metaTitle);
      formDataToSend.append("metaDescription", formData.metaDescription);
      formDataToSend.append("altTag", formData.altTag);
      formDataToSend.append("isScheduled", formData.isScheduled);
      formDataToSend.append("scheduledDate", formData.scheduledDate);
      formData.blog_tag_ids.forEach((tagId) => {
        formDataToSend.append("blogTagIds", tagId);
      });

      formDataToSend.append("description", formData.description);

      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      for (let [key, value] of formDataToSend.entries()) {
      }
      console.log(blogId);

      const response = await updateBlogApi(formDataToSend, blogId);
      toast.success(response.message || "Blog created successfully!");
      navigate("/blog");
      setFormData({
        title: "",
        blog_category_id: "",
        blog_tag_ids: [],
        description: "",
        image: null,
        slug: "",
        isScheduled: false,
        scheduledDate: ''
      });
      setImagePreview(null);

      const fileInput = document.getElementById("upload-file");
      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create blog. Please check your network connection.";
      toast.error(errorMessage);
    } finally {
      setCreatingBlog(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter blog title");
      return;
    }

    if (!formData.blog_category_id) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.slug.trim()) {
      toast.error("Please enter blog slug");
      return;
    }

    if (formData.blog_tag_ids.length === 0) {
      toast.error("Please select at least one tag");
      return;
    }

    if (!formData.description.trim()) {
      toast.error("Please enter blog description");
      return;
    }
    if (!formData.image) {
      toast.error("Please upload a blog image");
      return;
    }
    if (!formData.altTag) {
      toast.error("Please enter alt tag");
      return;
    }
    createBlog();
  };

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      blog_tag_ids: prev.blog_tag_ids.includes(tagId)
        ? prev.blog_tag_ids.filter((id) => id !== tagId)
        : [...prev.blog_tag_ids, tagId],
    }));
  };

  const removeTag = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      blog_tag_ids: prev.blog_tag_ids.filter((id) => id !== tagId),
    }));
  };
  const [searchParams] = useSearchParams();
  const blogId = searchParams.get("id");

  const [currentBlog, setCurrentBlog] = useState(null);

  const [isPageLoading, setIsPageLoading] = useState(false);

  const [blogData, setBlogData] = useState([]);

  const getAllBlogs = async () => {
    try {
      setIsPageLoading(true);
      const response = await getAllBlogsApi();

      setBlogData(response.data);

      if (blogId && response.data) {
        console.log("RESPONSE DATA ::", response.data.blogs);

        const specificBlog = response.data.blogs.find(
          (blog) => blog.id === blogId
        );
        setCurrentBlog(specificBlog);
      }
    } catch (error) {
      console.log("SERVER ::", error);

      toast.error("Failed to fetch blogs");
      setBlogData([]);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    getAllBlogs();
  }, [blogId]);

  useEffect(() => {
    if (currentBlog) {
      console.log("CURRENT BLOG ::", currentBlog);
      const formattedDate = currentBlog.scheduledDate
        ? new Date(currentBlog.scheduledDate).toISOString().slice(0, 16)
        : "";
      setFormData({
        title: currentBlog.title || "",
        blog_category_id: currentBlog.blogCategory.id || "",
        blog_tag_ids: currentBlog.blogTagIds || [],
        description: currentBlog.description || "",
        image: currentBlog.image || null,
        metaTitle: currentBlog?.metaTitle || "",
        metaDescription: currentBlog?.metaDescription || "",
        slug: currentBlog?.slug || "",
        altTag: currentBlog?.altTag || "",
        isScheduled: currentBlog.isScheduled || false,
        scheduledDate: formattedDate || "",
      });
    }
  }, [currentBlog]);
  const handleScheduleToggle = (e) => {
    const isChecked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      isScheduled: isChecked,
      scheduledDate: isChecked ? prev.scheduledDate : "",
    }));
  };
  const handleDateChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      scheduledDate: e.target.value,
    }));
  };
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };
  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card mt-24">
          <div className="card-header border-bottom">
            <h6 className="text-xl mb-0">Edit Blog</h6>
          </div>
          <div className="card-body p-24">
            <form onSubmit={handleSubmit} className="d-flex flex-column gap-20">
              {/* Blog Title */}
              <div>
                <label
                  className="form-label fw-bold text-neutral-900"
                  htmlFor="title"
                >
                  Blog Title:
                </label>
                <input
                  type="text"
                  name="title"
                  className="form-control border border-neutral-200 radius-8"
                  id="title"
                  placeholder="Enter Post Title"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>

              {/* Blog Category */}
              <div>
                <label className="form-label fw-bold text-neutral-900">
                  Blog Category:
                </label>
                <select
                  name="blog_category_id"
                  className="form-control border border-neutral-200 radius-8"
                  value={formData.blog_category_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label fw-bold text-neutral-900">
                  Blog Tags:
                </label>
                {formData.blog_tag_ids.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                      {formData.blog_tag_ids.map((tagId) => {
                        const tag = blogTags.find((t) => t.id === tagId);
                        return (
                          <span
                            key={tagId}
                            className="badge bg-primary-100 text-primary-600 px-3 py-2 rounded-pill d-flex align-items-center gap-1"
                            style={{ fontSize: "14px" }}
                          >
                            {tag?.tagName}
                            <button
                              type="button"
                              className="btn-close btn-close-sm ms-1"
                              style={{
                                fontSize: "10px",
                                width: "12px",
                                height: "12px",
                              }}
                              onClick={() => removeTag(tagId)}
                              aria-label="Remove tag"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tags Selection */}
                <div
                  className="border border-neutral-200 radius-8 p-3"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {blogTags.length > 0 ? (
                    <div className="row g-2">
                      {blogTags.map((tag) => (
                        <div key={tag.id} className="col-md-6 col-lg-4">
                          <div
                            className="form-check"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`tag-${tag.id}`}
                              checked={formData.blog_tag_ids.includes(tag.id)}
                              onChange={() => handleTagToggle(tag.id)}
                            />
                            <label
                              className="form-check-label text-sm"
                              htmlFor={`tag-${tag.id}`}
                              style={{ cursor: "pointer" }}
                            >
                              {tag.tagName}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center mb-0">
                      No tags available
                    </p>
                  )}
                </div>

                {/* Selection Counter */}
                <small className="text-muted mt-1 d-block">
                  {formData.blog_tag_ids.length} tag
                  {formData.blog_tag_ids.length !== 1 ? "s" : ""} selected
                </small>
              </div>

              {/* Blog Description */}
              {/* <div>
                                <label className='form-label fw-bold text-neutral-900'>
                                    Blog Description
                                </label>
                                <div className='border border-neutral-200 radius-8 overflow-hidden'>
                                    <div className='height-200'>
                                        <div id='toolbar-container'>
                                            <span className='ql-formats'>
                                                <select className='ql-font'></select>
                                                <select className='ql-size'></select>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-bold'></button>
                                                <button className='ql-italic'></button>
                                                <button className='ql-underline'></button>
                                                <button className='ql-strike'></button>
                                            </span>
                                            <span className='ql-formats'>
                                                <select className='ql-color'></select>
                                                <select className='ql-background'></select>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-script' value='sub'></button>
                                                <button className='ql-script' value='super'></button>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-header' value='1'></button>
                                                <button className='ql-header' value='2'></button>
                                                <button className='ql-blockquote'></button>
                                                <button className='ql-code-block'></button>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-list' value='ordered'></button>
                                                <button className='ql-list' value='bullet'></button>
                                                <button className='ql-indent' value='-1'></button>
                                                <button className='ql-indent' value='+1'></button>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-direction' value='rtl'></button>
                                                <select className='ql-align'></select>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-link'></button>
                                                <button className='ql-image'></button>
                                                <button className='ql-video'></button>
                                                <button className='ql-formula'></button>
                                            </span>
                                            <span className='ql-formats'>
                                                <button className='ql-clean'></button>
                                            </span>
                                        </div>

                                        <ReactQuill
                                            ref={quillRef}
                                            theme='snow'
                                            value={formData.description}
                                            onChange={handleDescriptionChange}
                                            modules={modules}
                                            formats={formats}
                                            placeholder='Compose an epic...'
                                        />
                                    </div>
                                </div>
                            </div> */}

              <div>
                <label className="form-label fw-bold text-neutral-900">
                  Blog Description
                </label>

                <div className="border border-neutral-200 radius-8 overflow-hidden">
                  <div className="height-200">
                    <div id="toolbar-container">
                      <span className="ql-formats">
                        <select className="ql-font"></select>
                        <select className="ql-size"></select>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-bold"></button>
                        <button className="ql-italic"></button>
                        <button className="ql-underline"></button>
                        <button className="ql-strike"></button>
                      </span>
                      <span className="ql-formats">
                        <select className="ql-color"></select>
                        <select className="ql-background"></select>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-script" value="sub"></button>
                        <button className="ql-script" value="super"></button>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-header" value="1"></button>
                        <button className="ql-header" value="2"></button>
                        <button className="ql-blockquote"></button>
                        <button className="ql-code-block"></button>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-list" value="ordered"></button>
                        <button className="ql-list" value="bullet"></button>
                        <button className="ql-indent" value="-1"></button>
                        <button className="ql-indent" value="+1"></button>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-direction" value="rtl"></button>
                        <select className="ql-align"></select>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-link"></button>
                        <button className="ql-image"></button>
                        <button className="ql-video"></button>
                        <button className="ql-formula"></button>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-clean"></button>
                      </span>
                    </div>

                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={formData.description}
                      onChange={handleDescriptionChange}
                      modules={modules}
                      formats={formats}
                      placeholder="Compose an epic..."
                    />
                  </div>
                </div>

                {/* Display the HTML output */}
                {/* <div style={{ marginTop: '20px' }}>
                                    <h4>HTML Output:</h4>
                                    <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto' }}>
                                        {formData.description}
                                    </pre>
                                </div> */}
              </div>

              <div>
                <label
                  className="form-label fw-bold text-neutral-900"
                  htmlFor="slug"
                >
                  Slug
                </label>
                <input
                  type="text"
                  name="slug"
                  className="form-control border border-neutral-200 radius-8"
                  id="slug"
                  placeholder="Enter slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label
                  className="form-label fw-bold text-neutral-900"
                  htmlFor="metaTitle"
                >
                  Meta Title:
                </label>
                <input
                  type="text"
                  name="metaTitle"
                  className="form-control border border-neutral-200 radius-8"
                  id="metaTitle"
                  placeholder="Enter Meta Title"
                  value={formData.metaTitle}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label
                  className="form-label fw-bold text-neutral-900"
                  htmlFor="metaDescription"
                >
                  Meta Description:
                </label>
                <textarea
                  type="text"
                  rows={3}
                  name="metaDescription"
                  className="form-control border border-neutral-200 radius-8"
                  id="metaDescription"
                  placeholder="Enter Meta Description"
                  value={formData.metaDescription}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label
                  className="form-label fw-bold text-neutral-900"
                  htmlFor="metaTitle"
                >
                  Alt Tag:
                </label>
                <input
                  type="text"
                  name="altTag"
                  className="form-control border border-neutral-200 radius-8"
                  id="metaTitle"
                  placeholder="Enter Alt Tag"
                  value={formData.altTag}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="scheduleToggle"
                    checked={formData.isScheduled}
                    onChange={handleScheduleToggle}
                  />
                  <label
                    className="form-check-label fw-bold text-neutral-900"
                    htmlFor="scheduleToggle"
                  >
                    Schedule this blog for later?
                  </label>
                </div>
              </div>

              {/* Date Picker - Only shows when scheduled is enabled */}
              {formData.isScheduled && (
                <div>
                  <label
                    className="form-label fw-bold text-neutral-900"
                    htmlFor="scheduledDate"
                  >
                    Scheduled Date & Time:
                  </label>
                  <input
                    type="datetime-local"
                    name="scheduledDate"
                    className="form-control border border-neutral-200 radius-8"
                    id="scheduledDate"
                    value={formData.scheduledDate}
                    onChange={handleDateChange}
                    min={getMinDateTime()}
                  />
                  {formData.scheduledDate && (
                    <small className="text-muted d-block mt-2">
                      Blog will be published on:{" "}
                      {new Date(formData.scheduledDate).toLocaleString()}
                    </small>
                  )}
                </div>
              )}

              {/* Blog Image */}
              <div>
                <label className="form-label fw-bold text-neutral-900">
                  Blog Image
                </label>
                <div className="upload-image-wrapper">
                  {imagePreview || formData.image ? (
                    <div className="uploaded-img position-relative h-160-px w-100 border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-50">
                      <button
                        type="button"
                        className="uploaded-img__remove position-absolute top-0 end-0 z-1 text-2xxl line-height-1 me-8 mt-8 d-flex bg-danger-600 w-40-px h-40-px justify-content-center align-items-center rounded-circle"
                        onClick={handleRemoveImage}
                      >
                        <Icon
                          icon="radix-icons:cross-2"
                          className="text-2xl text-white"
                        />
                      </button>
                      <img
                        id="uploaded-img__preview"
                        className="w-100 h-100 object-fit-cover"
                        src={
                          imagePreview
                            ? imagePreview
                            : `${IMG_URL}/${formData.image}`
                        }
                        alt="Uploaded"
                      />
                    </div>
                  ) : (
                    <label
                      className="upload-file h-160-px w-100 border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-50 bg-hover-neutral-200 d-flex align-items-center flex-column justify-content-center gap-1"
                      htmlFor="upload-file"
                    >
                      <iconify-icon
                        icon="solar:camera-outline"
                        className="text-xl text-secondary-light"
                      ></iconify-icon>
                      <span className="fw-semibold text-secondary-light">
                        Upload
                      </span>
                      <input
                        id="upload-file"
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleFileChange}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary-600 radius-8"
                disabled={creatingBlog}
              >
                {creatingBlog ? "Creating..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* Sidebar Start */}
    </div>
  );
};

export default EditBlog;
